import { useEffect, useRef, useState } from 'react';
import anime from 'animejs';
import type { CVConfig } from '../../config/cvConfig';

interface ContactProps {
  personal: CVConfig['personal'];
}

export function Contact({ personal }: ContactProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate contact elements
            anime({
              targets: '.contact-item',
              translateY: [50, 0],
              opacity: [0, 1],
              duration: 800,
              delay: (_el, i) => i * 200,
              easing: 'easeOutQuart'
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create mailto link with form data
    const subject = encodeURIComponent(formData.subject || 'Contact from Portfolio');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:${personal.email}?subject=${subject}&body=${body}`;
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className="py-20 px-4 sm:px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-orbitron font-bold neon-text mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-white/70 font-rajdhani max-w-3xl mx-auto">
            Ready to revolutionize your content strategy with AI? Let's create something extraordinary together.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="contact-item opacity-0">
              <h3 className="text-2xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-6">
                Let's Connect
              </h3>
              <p className="text-lg font-rajdhani text-white/80 leading-relaxed mb-8">
                Whether you're looking to scale your content production, implement AI-driven solutions, 
                or explore the cutting edge of automated creativity, I'm here to help bring your vision to life.
              </p>
            </div>

            {/* Contact Details */}
            <div className="contact-item opacity-0 space-y-6">
              <div className="flex items-center p-4 bg-black/40 backdrop-blur-sm rounded-xl neon-border">
                <div className="w-12 h-12 bg-[var(--synthwave-primary)]/20 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-[var(--synthwave-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-rajdhani font-semibold text-white">Email</div>
                  <div className="font-rajdhani text-[var(--synthwave-neon)]">{personal.email}</div>
                </div>
              </div>

              {personal.phone && (
                <div className="flex items-center p-4 bg-black/40 backdrop-blur-sm rounded-xl neon-border">
                  <div className="w-12 h-12 bg-[var(--synthwave-secondary)]/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-[var(--synthwave-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-rajdhani font-semibold text-white">Phone</div>
                    <div className="font-rajdhani text-[var(--synthwave-neon)]">{personal.phone}</div>
                  </div>
                </div>
              )}

              <div className="flex items-center p-4 bg-black/40 backdrop-blur-sm rounded-xl neon-border">
                <div className="w-12 h-12 bg-[var(--synthwave-accent)]/20 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-[var(--synthwave-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-rajdhani font-semibold text-white">Location</div>
                  <div className="font-rajdhani text-[var(--synthwave-neon)]">{personal.location}</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="contact-item opacity-0">
              <h4 className="text-xl font-orbitron font-bold text-[var(--synthwave-secondary)] mb-4">
                Follow Me
              </h4>
              <div className="flex space-x-4">
                {personal.socialLinks.github && (
                  <a
                    href={personal.socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-black/40 backdrop-blur-sm rounded-xl neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
                  >
                    <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                )}
                
                {personal.socialLinks.linkedin && (
                  <a
                    href={personal.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-black/40 backdrop-blur-sm rounded-xl neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
                  >
                    <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                )}

                {personal.socialLinks.twitter && (
                  <a
                    href={personal.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-black/40 backdrop-blur-sm rounded-xl neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
                  >
                    <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}

                {personal.socialLinks.website && (
                  <a
                    href={personal.socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-black/40 backdrop-blur-sm rounded-xl neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
                  >
                    <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9 3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-item opacity-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-rajdhani font-semibold text-white mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl text-white font-rajdhani focus:border-[var(--synthwave-neon)] focus:outline-none transition-colors duration-300"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-rajdhani font-semibold text-white mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl text-white font-rajdhani focus:border-[var(--synthwave-neon)] focus:outline-none transition-colors duration-300"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-rajdhani font-semibold text-white mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl text-white font-rajdhani focus:border-[var(--synthwave-neon)] focus:outline-none transition-colors duration-300"
                  placeholder="Project inquiry, collaboration, etc."
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-rajdhani font-semibold text-white mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl text-white font-rajdhani focus:border-[var(--synthwave-neon)] focus:outline-none transition-colors duration-300 resize-none"
                  placeholder="Tell me about your project, goals, and how I can help..."
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-[var(--synthwave-primary)] to-[var(--synthwave-secondary)] text-black font-rajdhani font-bold text-lg rounded-xl hover:from-[var(--synthwave-secondary)] hover:to-[var(--synthwave-accent)] transition-all duration-300 transform hover:scale-105 neon-border"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}